import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Check, CheckSquare, ChevronDown, ListChecks, Square, SquareDashed, X } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/* Shared stores let a selection follow the user between pages (Albums ⇄ Artists). */
const stores = {};
const storeSubs = new Set();
const subscribeStore = (f) => { storeSubs.add(f); return () => storeSubs.delete(f); };
const getStore = (k) => (stores[k] ||= { mode: false, sel: new Set() });
const patchStore = (k, patch) => {
  stores[k] = { ...getStore(k), ...patch };
  if ("mode" in patch) Object.keys(stores).forEach((o) => { stores[o] = { ...stores[o], mode: patch.mode }; }); // select mode is one switch across pages
  storeSubs.forEach((f) => f());
};
["albums", "artists"].forEach(getStore);
export const clearSharedSelection = () => { Object.keys(stores).forEach((k) => { stores[k] = { mode: false, sel: new Set() }; }); storeSubs.forEach((f) => f()); };
export const useSharedSelection = (k) => useSyncExternalStore(subscribeStore, () => getStore(k));
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
export const crossLabel = (n, noun, m, otherNoun) => (m ? (n ? `${plural(n, noun)} + ${plural(m, otherNoun)}` : plural(m, otherNoun)) : plural(n, noun));

/* Selection state shared by the grids. `ids` = all selectable ids currently shown. Ctrl/⌘+A selects all while in select mode, Esc exits.
   Pass `shared` (a store key) to keep the selection alive across page hops. */
export const useSelection = (ids, shared = null) => {
  const [localMode, setLocalMode] = useState(false);
  const [localSel, setLocalSel] = useState(new Set());
  const ext = useSyncExternalStore(subscribeStore, () => getStore(shared || "__local"));
  const mode = shared ? ext.mode : localMode;
  const sel = shared ? ext.sel : localSel;
  const setMode = useCallback((v) => (shared ? patchStore(shared, { mode: v }) : setLocalMode(v)), [shared]);
  const setSel = useCallback((fn) => {
    if (!shared) return setLocalSel(fn);
    patchStore(shared, { sel: typeof fn === "function" ? fn(getStore(shared).sel) : fn });
  }, [shared]);
  const anchor = useRef(null);
  // Shift+click selects the whole run between the last clicked item and this one.
  const toggle = useCallback((id, shift = false) => {
    setSel((s) => {
      const n = new Set(s);
      if (shift && anchor.current != null && anchor.current !== id && ids.includes(anchor.current) && ids.includes(id)) {
        const [a, b] = [ids.indexOf(anchor.current), ids.indexOf(id)].sort((x, y) => x - y);
        ids.slice(a, b + 1).forEach((x) => n.add(x));
      } else {
        n.has(id) ? n.delete(id) : n.add(id);
      }
      return n;
    });
    anchor.current = id;
  }, [ids, setSel]);
  const all = useCallback(() => { setMode(true); setSel((s) => new Set([...s, ...ids])); }, [ids, setMode, setSel]);
  const none = useCallback(() => { if (shared) clearSharedSelection(); else setSel(new Set()); if (shared) setMode(true); }, [shared, setSel, setMode]);
  const invert = useCallback(() => { setMode(true); setSel((s) => { const shownSet = new Set(ids); return new Set([...[...s].filter((id) => !shownSet.has(id)), ...ids.filter((id) => !s.has(id))]); }); }, [ids, setMode, setSel]);
  const where = useCallback((pred, items) => { setMode(true); setSel((s) => { const shownSet = new Set(items.map((x) => x.id ?? x.key ?? x.name)); return new Set([...[...s].filter((id) => !shownSet.has(id)), ...items.filter(pred).map((x) => x.id ?? x.key ?? x.name)]); }); }, [setMode, setSel]);
  const exit = useCallback(() => { if (shared) clearSharedSelection(); else { setMode(false); setSel(new Set()); } }, [shared, setMode, setSel]);
  const replace = useCallback((next) => { setMode(true); setSel(new Set(next)); }, [setMode, setSel]);
  useEffect(() => {
    if (!mode) return undefined;
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || "") || document.activeElement?.isContentEditable;
      if (e.key === "Escape") { if (!document.querySelector('[role="menu"], [role="dialog"]')) exit(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a" && !typing) { e.preventDefault(); all(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, all, exit]);
  // Selection survives sort changes, paging and filters; items not currently shown are counted as `hidden`.
  const shown = new Set(ids);
  const hidden = [...sel].filter((id) => !shown.has(id)).length;
  const allShown = ids.length > 0 && ids.every((id) => sel.has(id));
  return { mode, setMode, sel, toggle, all, none, invert, where, exit, replace, hidden, allShown, has: (id) => sel.has(id), count: sel.size, total: ids.length };
};

/* Drag a box across the children to lasso cards. Cards must carry data-sel-id. Hold Shift/Ctrl to add to the current selection. */
export const LassoArea = ({ s, children, className = "", testid = "lasso-area" }) => {
  const ref = useRef(null);
  const drag = useRef(null);
  const [box, setBox] = useState(null);
  const swallow = useRef(false);
  const onDown = (e) => {
    if (!s.mode || e.button !== 0 || e.target.closest("button, input, [role=menu]")) return;
    drag.current = { x: e.clientX, y: e.clientY, active: false, base: e.shiftKey || e.ctrlKey || e.metaKey ? [...s.sel] : [] };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    if (!d.active) {
      if (Math.hypot(e.clientX - d.x, e.clientY - d.y) < 6) return;
      d.active = true;
      ref.current.setPointerCapture?.(e.pointerId);
    }
    e.preventDefault();
    const r = { left: Math.min(d.x, e.clientX), top: Math.min(d.y, e.clientY), right: Math.max(d.x, e.clientX), bottom: Math.max(d.y, e.clientY) };
    setBox(r);
    const hit = [...ref.current.querySelectorAll("[data-sel-id]")].filter((el) => {
      const b = el.getBoundingClientRect();
      return b.left < r.right && b.right > r.left && b.top < r.bottom && b.bottom > r.top;
    }).map((el) => el.dataset.selId);
    s.replace([...d.base, ...hit]);
  };
  const onUp = (e) => {
    const d = drag.current;
    drag.current = null;
    setBox(null);
    if (d?.active) { swallow.current = true; ref.current.releasePointerCapture?.(e.pointerId); }
  };
  const onClickCapture = (e) => { if (swallow.current) { swallow.current = false; e.preventDefault(); e.stopPropagation(); } };
  return (
    <div ref={ref} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onClickCapture={onClickCapture}
      onDragStart={(e) => { if (s.mode) e.preventDefault(); }}
      className={`${className} ${s.mode ? "select-none [touch-action:none]" : ""}`} data-testid={testid} data-lasso={box ? "1" : undefined}>
      {children}
      {box && <div className="pointer-events-none fixed z-50 rounded-sm border border-zinc-900/60 bg-zinc-900/10" data-testid="lasso-box"
        style={{ left: box.left, top: box.top, width: box.right - box.left, height: box.bottom - box.top }} />}
    </div>
  );
};

export const SelectMark = ({ on, className = "left-3 top-3" }) => (
  <span className={`absolute ${className} z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 shadow transition-colors ${on ? "border-zinc-900 bg-zinc-900 text-white" : "border-white bg-white/80 text-transparent"}`} data-selected={on ? "1" : undefined}>
    <Check size={13} strokeWidth={3} />
  </span>
);

/* "Select ▾" dropdown: all / none / invert / smart picks / exit. `picks` = [{ label, onClick, testid, icon }]. */
export const SelectMenu = ({ s, noun, picks = [], testid = "select-menu", size = "h-10 text-sm" }) => {
  const label = s.mode ? (s.count ? `${s.count} selected` : "Selecting…") : "Select";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={`${testid}-trigger`} aria-pressed={s.mode}
        className={`flex ${size} items-center gap-2 rounded-full border px-4 transition-colors ${s.mode ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900"}`}>
        <ListChecks size={15} />{label}<ChevronDown size={13} className="opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60" data-testid={testid}>
        <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{s.total} {noun}{s.total === 1 ? "" : "s"} shown</DropdownMenuLabel>
        {!s.mode && s.setMode && <DropdownMenuItem onClick={() => s.setMode(true)} data-testid={`${testid}-start`}><ListChecks size={14} className="mr-2" />Pick one by one</DropdownMenuItem>}
        <DropdownMenuItem onClick={s.all} disabled={!s.total || s.count === s.total} data-testid={`${testid}-all`}><CheckSquare size={14} className="mr-2" />Select all<span className="ml-auto font-mono text-[10px] text-zinc-400">⌘A</span></DropdownMenuItem>
        <DropdownMenuItem onClick={s.none} disabled={!s.count} data-testid={`${testid}-none`}><Square size={14} className="mr-2" />Select none</DropdownMenuItem>
        <DropdownMenuItem onClick={s.invert} disabled={!s.total} data-testid={`${testid}-invert`}><SquareDashed size={14} className="mr-2" />Invert selection</DropdownMenuItem>
        {picks.length > 0 && <DropdownMenuSeparator />}
        {picks.map((p) => (
          <DropdownMenuItem key={p.testid} onClick={p.onClick} disabled={p.disabled || p.count === 0} data-testid={`${testid}-${p.testid}`}>{p.icon ? <p.icon size={14} className="mr-2" /> : <span className="mr-2 w-3.5" />}{p.label}{p.count != null && <span className="ml-auto font-mono text-[10px] text-zinc-400">{p.count}</span>}</DropdownMenuItem>
        ))}
        {s.mode && <><DropdownMenuSeparator /><DropdownMenuItem onClick={s.exit} data-testid={`${testid}-done`}><X size={14} className="mr-2" />Done selecting<span className="ml-auto font-mono text-[10px] text-zinc-400">Esc</span></DropdownMenuItem></>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const BarButton = ({ onClick, icon: Icon, children, testid, tone = "", disabled, title }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid} title={title}
    className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-xs transition-colors disabled:opacity-50 ${tone === "danger" ? "bg-red-600 px-4 font-medium text-white hover:bg-red-700" : tone === "accent" ? "border border-amber-300 bg-amber-50 font-medium text-amber-900 hover:border-amber-600" : "text-zinc-600 hover:bg-zinc-100"}`}>
    {Icon && <Icon size={14} />}{children}
  </button>
);
